import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown } from "react-icons/fi";
import clsx from "clsx";

interface SelectProps {
  label: string;
  options: { label: string; value: string }[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  openUpwards?: boolean;
}

const OnboardingSelect: React.FC<SelectProps> = ({ 
  label, 
  options, 
  value = "", 
  onChange,
  placeholder = "Select your state",
  error,
  disabled = false,
  openUpwards = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div ref={dropdownRef} className="w-full flex flex-col relative select-none">
      <label className="font-medium text-sm text-[#191C1D] mb-2 font-['Proxima_Nova']">
        {label}
      </label>
      
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={clsx(
          "w-full bg-[#F8F9FA] border rounded-lg min-h-[52px] px-4 py-3 text-base font-['Inter',_sans-serif] flex justify-between items-center outline-none gap-2",
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          error ? "border-red-500" : "border-[#A2B2C866]"
        )}
      >
        <span className={`truncate flex-1 text-left ${selectedOption ? "text-[#030724]" : "text-[#A2B2C899]"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        
        <div className={`shrink-0 transition-transform duration-200 flex items-center justify-center text-[#606685] ${isOpen ? "rotate-180" : "rotate-0"}`}>
          <FiChevronDown size={20} strokeWidth={2.5} />
        </div>
      </div>

      {isOpen && (
        <ul className={clsx(
          "absolute left-0 right-0 bg-white border border-[#A2B2C866] rounded-lg shadow-[0_4px_20px_rgba(3,7,36,0.08)] max-h-[300px] overflow-y-auto overflow-x-hidden z-50 animate-in fade-in duration-100",
          openUpwards ? "bottom-[calc(100%+8px)]" : "top-[calc(100%+8px)]"
        )}>
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => {
                if (onChange) onChange(opt.value);
                setIsOpen(false);
              }}
              className={`px-4 py-3 text-base font-['Inter',_sans-serif] cursor-pointer transition-colors duration-150 hover:bg-[#F3F4F5] break-all whitespace-normal ${
                value === opt.value ? "text-[#030724] font-semibold bg-[#F8F9FA]" : "text-[#46464D]"
              }`}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <span className="mt-1 block text-xs font-medium text-red-500 font-['Proxima_Nova']">
          {error}
        </span>
      )}
    </div>
  );
};

export default OnboardingSelect;